<i18n
  src="@/locales/views/agro_exporter/finances/container_outcomes/new_org_invoice_payment.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/org_invoice_discount.yml" lang="yaml"></i18n>
<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('show_org_invoice')"
    :params="{
      agro_exporter_id: agro_exporter_id,
      agro_exporter_process_id: agro_exporter_process_id,
      container_id: org_invoice.container_id,
      org_invoice_id: org_invoice_id
    }"
  />

  <bread-crumbs :labels="[module_name, agro_exporter_process.name]" />

  <org-invoice-info v-if="load_general_data" :org_invoice="org_invoice" />

  <alert-danger class="mt-6" :errors_data="std_err" />

  <div v-if="load_general_data" class="mt-8">
    <div class="panel">
      <div class="panel-header">
        <h3 class="panel-header-title">
          {{ $t("labels.title") }}
        </h3>
      </div>

      <div class="panel-body">
        <div class="space-y-8 divide-y divide-slate-200">
          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label :class="['label-form', isInvalidLabel('discount_code')]">
                {{ $t("model.org_invoice_discount.discount_code") }}
              </label>
              <div class="mt-1">
                <input type="text" :class="['input-text', isInvalid('discount_code')]" v-model.trim="discount_code" />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label :class="['label-form', isInvalidLabel('discount_at')]">
                {{ $t("model.org_invoice_discount.discount_at") }}
              </label>
              <div class="mt-1">
                <date-form v-model="discount_at" />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label
                :class="['label-form', isInvalidLabel('discount_percent')]"
              >
                {{ $t("model.org_invoice_discount.discount_percent") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model.number="discount_percent"
                  @input="automatic_percent = false"
                  :class="['input-text', isInvalid('discount_percent')]"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label :class="['label-form', isInvalidLabel('amount')]">
                {{ $t("model.org_invoice_discount.amount") }}
              </label>
              <div class="mt-1 flex w-full">
                <select-form
                  class="w-1/3"
                  :options="currencies"
                  v-model="currency_id"
                  :disabled="currencies.length == 1"
                  :is_invalid="isInvalidSelect('currency')"
                ></select-form>
                <div class="flex flex-1 relative">
                  <div
                    class="flex items-center absolute inset-y-0 left-0 ml-3 text-sm leading-5"
                  >
                    {{ currency_symbol }}
                  </div>
                  <input
                    type="number"
                    v-model.lazy.number="amount"
                    :class="['input-text pl-6', isInvalid('amount')]"
                  />
                </div>
              </div>
            </div>
            <div class="sm:col-span-3">
              <label :class="['label-form', isInvalidLabel('exchange_rate')]">
                {{ $t("model.org_invoice_discount.exchange_rate") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model.number="exchange_rate"
                  :class="['input-text', isInvalid('exchange_rate')]"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label :class="['label-form', isInvalidLabel('description')]">
                {{ $t("model.org_invoice_discount.description") }}
              </label>
              <div class="mt-1">
                <textarea
                  v-model="description"
                  id="description"
                  name="description"
                  rows="3"
                  :class="['input-text-area', isInvalid('description')]"
                  placeholder=""
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="panel-footer">
        <div class="panel-footer-actions">
          <white-button
            @click.prevent="goToShowExportInvoice()"
            :label="$t('labels.cancel_button')"
          />
          <span class="space-actions"></span>
          <success-button
            v-if="!proc_save_button"
            @click.prevent="createInvoiceDiscount()"
            :label="$t('labels.save_button')"
          />
          <success-button-processing v-else :label="$t('labels.save_button')" />
        </div>
      </div>
    </div>
  </div>
  <loading-message v-else />
</template>

<script>
import moment from "moment";
import Requester from "@/views/agro_exporter/requesters/finances/container_outcomes/manage_org_invoice_discount.js";
import OrgInvoiceInfo from "@/components/views/agro_exporter/finances/container_outcomes/OrgInvoiceInfo.vue";

export default {
  name: "AeFinancesContainerOutcomesNewInvoiceDiscount",
  components: { OrgInvoiceInfo },
  props: {
    agro_exporter_process_id: { required: true },
    agro_exporter_id: { required: true },
    org_invoice_id: { required: true }
  },
  data() {
    return {
      load_general_data: false,
      proc_save_button: false,
      agro_exporter_process: {},
      org_invoice: {},
      currencies: [],
      amount: "",
      discount_code: "",
      discount_at: "",
      exchange_rate: "",
      description: "",
      discount_percent: "",
      automatic_percent: true,
      currency_id: null
    };
  },
  methods: {
    fetchData: async function () {
      await this.fetchAgroExporterProcess();
      await this.fetchOrgInvoice();
      await this.fetchCurrencies();
      this.load_general_data = true;
    },
    fetchAgroExporterProcess: async function () {
      let response = await Requester.fetchAgroExporterProcess(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.agro_exporter_process = response.data;
    },
    fetchOrgInvoice: async function () {
      let response = await Requester.fetchOrgInvoice(
        this.agro_exporter_id,
        this.org_invoice_id
      );
      this.org_invoice = response.data;
    },
    fetchCurrencies: async function () {
      let response = await Requester.fetchCurrencies(this.agro_exporter_id);
      this.currencies = response.data;
      if (this.currencies.length == 1) this.currency_id = this.currencies[0].id;
    },
    createInvoiceDiscount: async function () {
      this.proc_save_button = true;
      try {
        let response = await Requester.create(
          this.agro_exporter_id,
          this.org_invoice_id,
          {
            discount_code: this.discount_code,
            discount_at: this.discount_at_formatted,
            amount: this.amount,
            exchange_rate: this.exchange_rate,
            description: this.description,
            discount_percent: this.discount_percent,
            currency_id: this.currency_id
          }
        );
        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowExportInvoice();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      this.proc_save_button = false;
    },
    goToShowExportInvoice: function () {
      this.$router.push({
        name: this.routeName("show_org_invoice"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id,
          org_invoice_id: this.org_invoice_id,
          container_id: this.org_invoice.container_id
        }
      });
    }
  },
  computed: {
    discount_at_formatted: function () {
      return moment(this.discount_at).format("YYYY-MM-DD");
    },
    currency_symbol: function () {
      return this.currencies.find(currency => currency.id == this.currency_id)
        .symbol;
    }
  },
  watch: {
    amount: function (amount) {
      if (this.automatic_percent) {
        let percent = amount / this.org_invoice.amount;
        this.discount_percent = parseInt(percent * 10000) / 100
      }
    }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id
        }
      }).then(() => this.fetchData())
    );
  }
};
</script>
